import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './ReadableAccordion';
import { AlertTriangle, CheckCircle, ClipboardCheck, Mail, MapPin, Phone, ShieldCheck, Star, Wrench } from 'lucide-react';
import InlineBookingForm from './InlineBookingForm';
import { HeroProofList } from './HeroProofList';
import { ProofMetric } from './ProofMetric';
import { TechnicianNetworkCta } from './TechnicianNetworkCta';
import { problemSeoPages } from '../data/seoProblemPages';
import { companyInfo, contactInfo, googleReviewsData } from '../data/mockData';
import { trackEvent } from '../utils/analytics';
import { setPageSeo } from '../utils/seo';
import { scrollToBookingForm } from '../utils/bookingFormHighlight';

const kebab = (value) => value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

const problemLinks = [
  ['/3d-printer-blob-of-death-repair', 'Blob of Death Repair', 'problem-footer-blob-of-death-link'],
  ['/3d-printer-nozzle-clog-repair', 'Nozzle Clog Repair', 'problem-footer-nozzle-clog-link'],
  ['/3d-printer-layer-shifting-repair', 'Layer Shifting Repair', 'problem-footer-layer-shifting-link'],
  ['/3d-printer-bed-adhesion-repair', 'Bed Adhesion Repair', 'problem-footer-bed-adhesion-link'],
  ['/3d-printer-under-extrusion-repair', 'Under-Extrusion Repair', 'problem-footer-under-extrusion-link']
];

const buildProblemSchema = (page) => ({
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://repair.3dprintergear.com.au/' },
        { '@type': 'ListItem', position: 2, name: page.h1, item: page.canonical }
      ]
    },
    {
      '@type': 'TechArticle',
      '@id': `${page.canonical}#article`,
      headline: page.h1,
      description: page.description,
      image: page.image,
      author: { '@id': 'https://repair.3dprintergear.com.au/#business' },
      publisher: { '@id': 'https://repair.3dprintergear.com.au/#business' },
      mainEntityOfPage: { '@id': `${page.canonical}#webpage` },
      about: page.problemName,
      inLanguage: 'en-AU'
    },
    {
      '@type': 'WebPage',
      '@id': `${page.canonical}#webpage`,
      url: page.canonical,
      name: page.title,
      description: page.description,
      isPartOf: { '@id': 'https://repair.3dprintergear.com.au/#website' },
      about: { '@id': `${page.canonical}#service` },
      primaryImageOfPage: { '@type': 'ImageObject', url: page.image },
      inLanguage: 'en-AU'
    },
    {
      '@type': 'Service',
      '@id': `${page.canonical}#service`,
      name: page.h1,
      serviceType: `${page.problemName} 3D Printer Repair`,
      provider: { '@id': 'https://repair.3dprintergear.com.au/#business' },
      areaServed: { '@type': 'Country', name: 'Australia' },
      availableChannel: {
        '@type': 'ServiceChannel',
        servicePhone: { '@type': 'ContactPoint', telephone: '+611300334327', contactType: 'Repairs and service bookings' },
        serviceUrl: page.canonical
      },
      description: page.heroSubheading,
      offers: { '@type': 'Offer', availability: 'https://schema.org/InStock', priceCurrency: 'AUD', description: 'Free quote before repair work begins.' }
    },
    {
      '@type': 'FAQPage',
      '@id': `${page.canonical}#faq`,
      mainEntity: page.faqs.map((faq) => ({
        '@type': 'Question',
        name: faq.question,
        acceptedAnswer: { '@type': 'Answer', text: faq.answer }
      }))
    }
  ]
});

export default function SeoProblemPage({ problemKey = 'blobOfDeath' }) {
  const page = problemSeoPages[problemKey];
  const bookingFormRef = useRef(null);
  const [formInView, setFormInView] = useState(true);
  const [bookingPrefill, setBookingPrefill] = useState({ issueDescription: page.formPrefill, urgency: 'priority', autoApply: true, expand: false });
  const schema = useMemo(() => buildProblemSchema(page), [buildProblemSchema, page]);

  useEffect(() => {
    setPageSeo({ title: page.title, description: page.description, canonical: page.canonical, image: page.image });
  }, [page, setPageSeo]);

  useEffect(() => {
    const formNode = bookingFormRef.current;
    if (!formNode) return undefined;
    const observer = new IntersectionObserver(([entry]) => setFormInView(entry.isIntersecting), { threshold: 0.2 });
    observer.observe(formNode);
    return () => observer.disconnect();
  }, [bookingFormRef]);

  const scrollToBooking = (location, label = 'Start repair request', issueText = page.formPrefill) => {
    trackEvent('cta_click', { cta_location: location, cta_label: label });
    setBookingPrefill({ issueDescription: issueText, urgency: 'priority', sequence: Date.now() });
    scrollToBookingForm(bookingFormRef);
  };

  const handlePhoneClick = (phoneLocation) => trackEvent('phone_click', { phone_location: phoneLocation });

  return (
    <div className="min-h-screen bg-white text-gray-900 pb-20 md:pb-0" data-testid={`seo-problem-page-${page.slug}`}>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
      <header className="sticky top-0 z-50 bg-white border-b border-gray-200" data-testid="problem-page-header">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-3">
          <div className="flex items-center justify-between gap-4">
            <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity" data-testid="problem-header-home-link">
              <img src={companyInfo.logo} alt={companyInfo.name} className="h-10 sm:h-12 w-auto" data-testid="problem-header-logo-image" />
              <div className="hidden sm:block">
                <p className="text-sm font-extrabold tracking-tight" data-testid="problem-header-company-name">{companyInfo.name}</p>
                <p className="text-xs text-gray-500" data-testid="problem-header-company-division">Service &amp; Repair Division</p>
              </div>
            </Link>
            <div className="flex items-center gap-3 sm:gap-5">
              <a href="tel:1300334327" onClick={() => handlePhoneClick(`${page.slug}_header`)} className="hidden md:flex items-center gap-2 text-sm font-semibold text-gray-700 hover:text-black transition-colors" data-testid="problem-header-phone-link">
                <Phone className="h-4 w-4" />
                <span data-testid="problem-header-phone-text">{contactInfo.phone} <span className="text-gray-400">·</span> Option 2</span>
              </a>
              <button onClick={() => scrollToBooking(`${page.slug}_header`, 'Book Service')} className="bg-gray-900 hover:bg-black text-white text-sm font-bold px-3 sm:px-5 py-2.5 rounded-sm border-b-4 border-orange-500 hover:border-orange-400 transition-colors whitespace-nowrap" data-testid="problem-header-book-button">
                <span className="sm:hidden">Book</span><span className="hidden sm:inline">Book Service</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <main>
        <section className="border-b border-gray-200 bg-white" data-testid="problem-hero-section">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 py-8 lg:py-14">
            <div className="grid gap-8 lg:grid-cols-12 lg:gap-12 items-start">
              <div className="lg:col-span-5">
                <p className="text-xs font-extrabold uppercase tracking-[0.18em] text-gray-500 mb-3" data-testid="problem-hero-eyebrow">{page.eyebrow}</p>
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.02] mb-4" data-testid="problem-hero-heading">{page.h1}</h1>
                <p className="text-base text-gray-600 leading-relaxed max-w-xl mb-5" data-testid="problem-hero-subheading">{page.heroSubheading}</p>
                <HeroProofList items={page.proof} testId="problem-hero-proof-list" />
                <div className="border border-gray-200 overflow-hidden rounded-sm bg-gray-50" data-testid="problem-hero-image-wrap">
                  <img src={page.image} alt={`${page.problemName} 3D printer repair example`} className="w-full aspect-[16/9] object-cover object-center" data-testid="problem-hero-image" />
                </div>
              </div>
              <div className="lg:col-span-7 lg:sticky lg:top-24" data-testid="problem-booking-column">
                <InlineBookingForm key={bookingPrefill?.sequence || `${problemKey}-booking-form`} ref={bookingFormRef} prefill={bookingPrefill} pageSource={page.h1} />
              </div>
            </div>
          </div>
        </section>

        <section className="bg-gray-900 text-white" data-testid="problem-proof-strip">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 py-4">
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-px bg-gray-700 border border-gray-700">
              {[[page.problemName, 'Issue-specific repair'], ['AU-wide', 'Repair workflow'], ['Insured', 'PI + public liability'], ['Quote', 'Before work begins'], ['700+', 'Google reviews']].map(([number, label]) => <ProofMetric key={label} number={number} label={label} testIdPrefix="problem-proof-metric" />)}
            </div>
          </div>
        </section>

        <section className="py-14 bg-white" data-testid="problem-symptoms-section">
          <div className="mx-auto max-w-7xl px-4 sm:px-6">
            <div className="grid lg:grid-cols-12 gap-8">
              <div className="lg:col-span-4">
                <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight mb-3" data-testid="problem-symptoms-heading">Symptoms that match this issue</h2>
                <p className="text-base text-gray-600 leading-relaxed mb-5" data-testid="problem-symptoms-summary">If these signs look familiar, include them in the repair request so our team can triage the fault faster.</p>
                <button onClick={() => scrollToBooking(`${page.slug}_symptoms`, 'Start repair request')} className="bg-gray-900 hover:bg-black text-white text-sm font-bold px-5 py-2.5 rounded-sm border-b-4 border-orange-500 hover:border-orange-400 transition-colors" data-testid="problem-symptoms-cta-button">Start repair request</button>
              </div>
              <div className="lg:col-span-8 grid sm:grid-cols-2 gap-3">
                {page.symptoms.map((symptom) => (
                  <button key={symptom} onClick={() => scrollToBooking(`${page.slug}_symptom`, symptom, `I need help with ${page.problemName}: ${symptom}.`)} className="text-left border border-gray-200 bg-gray-50 hover:border-gray-900 p-5 rounded-sm transition-colors" data-testid={`problem-symptom-${kebab(symptom)}`}>
                    <AlertTriangle className="h-5 w-5 text-orange-500 mb-3" />
                    <span className="text-sm font-extrabold text-gray-900">{symptom}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-14 bg-gray-50 border-y border-gray-200" data-testid="problem-causes-checks-section">
          <div className="mx-auto max-w-7xl px-4 sm:px-6">
            <div className="grid lg:grid-cols-2 gap-8">
              <div data-testid="problem-causes-block">
                <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight mb-5" data-testid="problem-causes-heading">Common causes</h2>
                <div className="space-y-3">
                  {page.causes.map((cause) => (
                    <div key={cause} className="border border-gray-200 bg-white p-4 rounded-sm flex gap-3" data-testid={`problem-cause-${kebab(cause)}`}>
                      <Wrench className="h-5 w-5 text-gray-900 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-gray-700 leading-relaxed">{cause}</p>
                    </div>
                  ))}
                </div>
              </div>
              <div data-testid="problem-safe-checks-block">
                <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight mb-5" data-testid="problem-safe-checks-heading">Safe checks before booking</h2>
                <div className="space-y-3">
                  {page.safeChecks.map((check) => (
                    <div key={check} className="border border-gray-200 bg-white p-4 rounded-sm flex gap-3" data-testid={`problem-safe-check-${kebab(check)}`}>
                      <ClipboardCheck className="h-5 w-5 text-gray-900 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-gray-700 leading-relaxed">{check}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-14 bg-white" data-testid="problem-stop-diy-section">
          <div className="mx-auto max-w-7xl px-4 sm:px-6">
            <div className="grid lg:grid-cols-12 gap-8">
              <div className="lg:col-span-4">
                <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight mb-3" data-testid="problem-stop-diy-heading">When to stop DIY and book repair</h2>
                <p className="text-base text-gray-600 leading-relaxed" data-testid="problem-stop-diy-summary">Professional repair is usually cheaper than repeated trial-and-error once parts, wiring, hotends or business downtime are involved.</p>
              </div>
              <div className="lg:col-span-8 grid sm:grid-cols-2 gap-3">
                {page.stopDiy.map((item) => (
                  <article key={item} className="border border-gray-200 bg-gray-50 p-5 rounded-sm" data-testid={`problem-stop-diy-item-${kebab(item)}`}>
                    <CheckCircle className="h-5 w-5 text-orange-500 mb-3" />
                    <p className="text-sm font-extrabold text-gray-900">{item}</p>
                  </article>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-14 bg-gray-50 border-y border-gray-200" data-testid="problem-brands-section">
          <div className="mx-auto max-w-7xl px-4 sm:px-6">
            <div className="grid lg:grid-cols-12 gap-8">
              <div className="lg:col-span-4">
                <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight mb-3" data-testid="problem-brands-heading">Brands affected by {page.problemName}</h2>
                <p className="text-base text-gray-600 leading-relaxed" data-testid="problem-brands-summary">This issue can appear across hobby, school and professional printers. Brand and model details help us identify the safest repair path.</p>
              </div>
              <div className="lg:col-span-8 grid grid-cols-2 md:grid-cols-4 gap-px bg-gray-200 border border-gray-200" data-testid="problem-brands-grid">
                {page.affectedBrands.map((brand) => (
                  <div key={brand} className="bg-white p-4 min-h-[74px] flex items-center" data-testid={`problem-brand-${kebab(brand)}`}>
                    <p className="text-sm font-extrabold text-gray-900">{brand}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-14 bg-white" data-testid="problem-trust-section">
          <div className="mx-auto max-w-7xl px-4 sm:px-6">
            <div className="grid lg:grid-cols-12 gap-8 items-start">
              <div className="lg:col-span-4">
                <div className="flex items-center gap-2 mb-4" data-testid="problem-google-rating-summary"><span className="text-2xl font-extrabold">{googleReviewsData.rating}/5</span>{Array.from({ length: 5 }, (_, starNumber) => starNumber + 1).map((starNumber) => <Star key={`problem-summary-star-${starNumber}`} className="h-4 w-4 text-yellow-400 fill-current" />)}</div>
                <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight mb-3" data-testid="problem-trust-heading">Repair confidence before you book</h2>
                <p className="text-base text-gray-600" data-testid="problem-trust-summary">The goal is a safe diagnosis, clear quote and repair path that protects the printer instead of adding more risk.</p>
              </div>
              <div className="lg:col-span-8 grid sm:grid-cols-2 gap-3">
                {[[ShieldCheck, 'Fully insured', 'Professional indemnity and public liability cover.'], [Wrench, 'Repair-centre backed', 'Specialist diagnostics and parts workflow.'], [ClipboardCheck, 'Quote-before-work', 'No repair begins until the quote is clear.'], [CheckCircle, 'Schools and businesses', 'Support for serious users, classrooms and printer fleets.']].map(([Icon, title, copy]) => (
                  <article key={title} className="border border-gray-200 bg-gray-50 p-5 rounded-sm" data-testid={`problem-trust-card-${kebab(title)}`}>
                    <Icon className="h-6 w-6 text-gray-900 mb-3" />
                    <h3 className="text-base font-extrabold mb-1" data-testid={`problem-trust-card-title-${kebab(title)}`}>{title}</h3>
                    <p className="text-sm text-gray-600 leading-relaxed" data-testid={`problem-trust-card-copy-${kebab(title)}`}>{copy}</p>
                  </article>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-14 bg-gray-50 border-y border-gray-200" data-testid="problem-related-pages-section">
          <div className="mx-auto max-w-7xl px-4 sm:px-6">
            <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight mb-6" data-testid="problem-related-pages-heading">Related repair pages</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="problem-related-pages-grid">
              {[
                ['/bambu-lab-repair-australia', 'Bambu Lab Repair Australia'],
                ['/3d-printer-repair-melbourne', '3D Printer Repair Melbourne'],
                ['/3d-printer-repair-sydney', '3D Printer Repair Sydney'],
                ['/3d-printer-repair-brisbane', '3D Printer Repair Brisbane'],
                ['/3d-printer-repair-perth', '3D Printer Repair Perth'],
                ['/3d-printer-repair-adelaide', '3D Printer Repair Adelaide']
              ].map(([to, label]) => (
                <Link key={to} to={to} className="border border-gray-200 bg-white hover:bg-gray-50 hover:border-gray-900 p-5 rounded-sm transition-colors" data-testid={`problem-related-link-${kebab(label)}`}>
                  <span className="text-sm font-extrabold text-gray-900">{label}</span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 bg-white" data-testid="problem-faq-section">
          <div className="mx-auto max-w-7xl px-4 sm:px-6">
            <div className="grid lg:grid-cols-12 gap-8">
              <div className="lg:col-span-4">
                <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight mb-3" data-testid="problem-faq-heading">{page.problemName} repair questions</h2>
                <p className="text-base text-gray-600" data-testid="problem-faq-summary">Answers for printer owners deciding whether to keep troubleshooting or request professional repair support.</p>
              </div>
              <div className="lg:col-span-8">
                <Accordion type="single" collapsible className="border-t border-gray-200" data-testid="problem-faq-accordion" onValueChange={(value) => value && trackEvent('faq_open', { faq_id: value, faq_category: `${page.slug}_seo_page` })}>
                  {page.faqs.map((faq, index) => (
                    <AccordionItem key={faq.question} value={`${page.slug}-faq-${index + 1}`} className="border-b border-gray-200" data-testid={`problem-faq-item-${index + 1}`}>
                      <AccordionTrigger className="text-left py-4 hover:no-underline" data-testid={`problem-faq-question-${index + 1}`}><span className="text-sm font-extrabold text-gray-900 pr-4">{faq.question}</span></AccordionTrigger>
                      <AccordionContent className="text-sm text-gray-600 leading-relaxed pb-5" data-testid={`problem-faq-answer-${index + 1}`}>{faq.answer}</AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            </div>
          </div>
        </section>

        <TechnicianNetworkCta location={`${page.slug}-problem-page`} />

        <section className="py-14 bg-gray-900 text-white" data-testid="problem-final-cta-section">
          <div className="mx-auto max-w-4xl px-4 sm:px-6 text-center">
            <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight mb-3" data-testid="problem-final-cta-heading">Need help with {page.problemName}?</h2>
            <p className="text-base text-gray-400 mb-6" data-testid="problem-final-cta-summary">Start a repair request and include photos, printer model, material and what happened before the failure.</p>
            <button onClick={() => scrollToBooking(`${page.slug}_final_cta`, 'Start repair request')} className="bg-white text-gray-900 hover:bg-gray-100 text-sm font-extrabold px-6 py-3 rounded-sm border-b-4 border-orange-500 hover:border-orange-400 transition-colors" data-testid="problem-final-cta-button">Start repair request</button>
          </div>
        </section>
      </main>

      <footer className="bg-gray-900 text-white py-14 border-t border-gray-800" data-testid="problem-footer">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <div className="grid md:grid-cols-4 gap-8">
            <div><img src={companyInfo.logo} alt={companyInfo.name} className="h-10 w-auto filter brightness-0 invert mb-4" data-testid="problem-footer-logo" /><p className="text-gray-400 text-sm leading-relaxed" data-testid="problem-footer-description">Australia&apos;s trusted 3D printer service and repair team.</p></div>
            <div><h3 className="text-base font-extrabold mb-4" data-testid="problem-footer-contact-heading">Contact</h3><div className="space-y-3 text-sm text-gray-300"><p className="flex items-start gap-3" data-testid="problem-footer-address"><MapPin className="h-4 w-4 text-gray-500 mt-0.5 flex-shrink-0" />{contactInfo.address}</p><a href="tel:1300334327" onClick={() => handlePhoneClick(`${page.slug}_footer`)} className="flex items-center gap-3 hover:text-white" data-testid="problem-footer-phone-link"><Phone className="h-4 w-4 text-gray-500 flex-shrink-0" />{contactInfo.phone} ({contactInfo.phoneOption})</a><a href="mailto:admin@3dprintergear.com.au" onClick={() => trackEvent('email_click', { email_location: `${page.slug}_footer` })} className="flex items-center gap-3 hover:text-white" data-testid="problem-footer-email-link"><Mail className="h-4 w-4 text-gray-500 flex-shrink-0" />admin@3dprintergear.com.au</a></div></div>
            <div><h3 className="text-base font-extrabold mb-4" data-testid="problem-footer-service-pages-heading">Service pages</h3><div className="space-y-2 text-sm text-gray-300"><Link to="/" className="block hover:text-white" data-testid="problem-footer-national-link">3D Printer Repair Australia</Link><Link to="/bambu-lab-repair-australia" className="block hover:text-white" data-testid="problem-footer-bambu-link">Bambu Lab Repair Australia</Link><Link to="/3d-printer-repair-melbourne" className="block hover:text-white" data-testid="problem-footer-melbourne-link">3D Printer Repair Melbourne</Link><Link to="/3d-printer-repair-sydney" className="block hover:text-white" data-testid="problem-footer-sydney-link">3D Printer Repair Sydney</Link><Link to="/3d-printer-repairs-for-schools" className="block hover:text-white" data-testid="problem-footer-school-repairs-link">3D Printer Repairs for Schools</Link><Link to="/3d-printer-maintenance-for-schools" className="block hover:text-white" data-testid="problem-footer-school-maintenance-link">3D Printer Maintenance for Schools</Link><Link to="/apply-as-technician" className="block hover:text-white" data-testid="problem-footer-apply-as-technician-link">Apply as a Technician</Link></div></div>
            <div><h3 className="text-base font-extrabold mb-4" data-testid="problem-footer-problem-pages-heading">Problem repair pages</h3><div className="space-y-2 text-sm text-gray-300">{problemLinks.map(([to, label, testId]) => <Link key={to} to={to} className="block hover:text-white" data-testid={testId}>{label}</Link>)}</div></div>
          </div>
          <div className="border-t border-gray-800 mt-10 pt-6 text-sm text-gray-500" data-testid="problem-footer-copyright">{contactInfo.copyright} {companyInfo.name}. All rights reserved. | {contactInfo.abn} | <a href="https://www.3dprintergear.com.au/privacy-policy/" className="hover:text-gray-300 underline underline-offset-2" data-testid="problem-footer-privacy-policy-link">Privacy Policy</a></div>
        </div>
      </footer>

      {!formInView && <div className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 p-3" data-testid="problem-mobile-sticky-cta-wrap"><div className="grid grid-cols-[1fr_auto] gap-2"><button onClick={() => scrollToBooking(`${page.slug}_mobile_sticky`, 'Start Repair Form')} className="bg-gray-900 hover:bg-black text-white text-sm font-extrabold py-3 rounded-sm border-b-4 border-orange-500 hover:border-orange-400 transition-colors" data-testid="problem-mobile-sticky-start-form-button">Start Repair Form</button><a href="tel:1300334327" onClick={() => handlePhoneClick(`${page.slug}_mobile_sticky`)} className="border border-gray-300 text-gray-900 text-sm font-extrabold px-4 py-3 rounded-sm" data-testid="problem-mobile-sticky-call-button">Call Option 2</a></div></div>}
    </div>
  );
}